<template lang="pug">
div
  v-card-title
    span Potencial Comercial del Caso
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")
    .d-flex.align-baseline.flex-wrap.mb-2(style="gap:12px")
      h3.mb-0 Tamaño de mercado
      h1.primary--text.mb-0 {{ computedMarketSize | moneyMM }}
    p.text--secondary.mb-3 Proyecta tu mercado eligiendo el período y el horizonte (hasta 5 años). La estacionalidad reparte cada año en sus trimestres/semestres.
    v-row(dense)
      v-col(cols="6" md="3")
        v-select(label="Ver por" v-model="granularity" :items="granOptions" item-text="text" item-value="value" outlined dense hide-details @change="onGranularityChange")
      v-col(cols="6" md="3")
        v-text-field(label="N° de períodos" v-model.number="nPeriods" type="number" :min="1" :max="maxPeriods" outlined dense :hint="`máx ${maxPeriods} (5 años)`" persistent-hint)
      v-col(cols="6" md="3")
        v-select(label="Crecimiento" v-model="growthMode" :items="growthModes" item-text="text" item-value="value" outlined dense hide-details)
      v-col(cols="6" md="3")
        v-text-field(v-if="growthMode === 'lineal'" label="Tasa anual" v-model.number="growthRate" type="number" suffix="%" outlined dense hide-details)
    v-row(v-if="growthMode === 'porAnio'" dense).mt-1
      v-col(v-for="(r, idx) in horizonYears" :key="idx" cols="4" md="2")
        v-text-field(:label="`${startYear + idx + 1}`" v-model.number="yearlyRates[idx]" type="number" suffix="%" outlined dense hide-details)
    .text-right.mt-3
      v-btn(color="primary" depressed @click="updateChart")
        v-icon.mr-1(small) mdi-chart-timeline-variant
        | Proyectar
  v-card-text
    .chart-wrap(v-if="showProjection" style="position: relative; height: 340px; width: 100%")
      Bar.chart(:chart-data="chartData" :chart-options="chartOptions" :chart-id="id" :plugins="chartPlugins" :styles="{position:'relative',height:'100%',width:'100%'}")
    .empty-projection(v-else)
      p.text--secondary.mb-0 Carga un caso con datos para ver la proyección de tu mercado.
</template>

<script>
import { Bar } from 'vue-chartjs/legacy'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { mapActions } from 'vuex'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'
import { fmtMoneyMM } from '../../../utils/customVueFilters'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// Curva estacional por defecto (suma 100). Reparte el valor anual en meses;
// luego se agrega a trimestres/semestres.
const SEASONAL = [8, 7, 9, 9, 8, 6, 6, 7, 10, 10, 9, 11]

// Notación de dinero unificada (ver utils/customVueFilters fmtMoneyMM).
const fmtMM = fmtMoneyMM

export default {
  props: {
    filters: { type: Object, required: true, default: () => ({}) },
    projection: { type: Number, default: null },
  },
  components: { Bar, InfoAlert, ShowInfoBtn },
  data() {
    return {
      id: this._uid.toString(),
      chartPlugins: [ChartDataLabels],
      alertMessage: `
<p>
El potencial comercial representa el <b>tamaño total de venta de tu mercado</b> según el caso ingresado. Combina tu Mercado Total Disponible (TAM) y tu Mercado Disponible Servible (SAM).
<br/><br/>
Elige la granularidad (trimestre, semestre o año), cuántos períodos ver (hasta 5 años) y cómo crece (lineal o distinto por año). Usa los filtros para segmentar por cliente, producto o línea.
</p>`,
      marketSize: 0,
      startYear: new Date().getFullYear(),
      granularity: 'anio',
      granOptions: [
        { text: 'Año', value: 'anio' },
        { text: 'Semestre', value: 'semestre' },
        { text: 'Trimestre', value: 'trimestre' },
      ],
      nPeriods: 5,
      growthMode: 'lineal',
      growthModes: [
        { text: 'Lineal (una tasa)', value: 'lineal' },
        { text: 'Distinta por año', value: 'porAnio' },
      ],
      growthRate: 0,
      yearlyRates: [0, 0, 0, 0, 0],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 28 } },
        scales: {
          y: {
            beginAtZero: true,
            grace: '15%',
            ticks: { callback: (value) => fmtMM(value) },
          },
        },
        plugins: {
          legend: { display: true, position: 'top' },
          tooltip: { callbacks: { label: (context) => fmtMM(context.raw) } },
          datalabels: {
            anchor: 'end', align: 'top', offset: 4, clip: false,
            color: '#1B2E38', font: { size: 11, weight: '600' },
            formatter: (value) => (value ? fmtMM(value) : null),
          },
        },
      },
      chartData: {
        labels: [],
        datasets: [
          {
            label: 'Potencial de mercado proyectado',
            data: [],
            backgroundColor: '#5575DF',
            hoverBackgroundColor: '#42589C',
            borderRadius: 6,
            maxBarThickness: 64,
          },
        ],
      },
    }
  },
  computed: {
    computedMarketSize() {
      return this.marketSize
    },
    periodsPerYear() {
      return this.granularity === 'trimestre' ? 4 : this.granularity === 'semestre' ? 2 : 1
    },
    maxPeriods() {
      return this.periodsPerYear * 5
    },
    // años del horizonte (para los inputs de tasa por año)
    horizonYears() {
      return Math.min(5, Math.ceil((Number(this.nPeriods) || 1) / this.periodsPerYear))
    },
    showProjection() {
      return this.marketSize > 0
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getTotalProjectionChart']),
    async fetchData() {
      const params = { ...this.filters, projection: this.projection || this.$route.params.id }
      const { data, error } = await this.getTotalProjectionChart(params)
      if (!error) {
        this.marketSize = data.total
        this.updateChart()
      }
    },
    onGranularityChange() {
      // ajusta el n° de períodos al máximo del nuevo nivel si se pasa
      if (Number(this.nPeriods) > this.maxPeriods) this.nPeriods = this.maxPeriods
      this.updateChart()
    },
    seasonalWeights() {
      const ppy = this.periodsPerYear
      if (ppy === 1) return [1]
      const total = SEASONAL.reduce((a, b) => a + b, 0)
      const monthsPer = 12 / ppy
      return Array.from({ length: ppy }, (_, i) => {
        const slice = SEASONAL.slice(i * monthsPer, i * monthsPer + monthsPer)
        return slice.reduce((a, b) => a + b, 0) / total
      })
    },
    yearFactor(year) {
      if (this.growthMode === 'lineal') {
        const g = (Number(this.growthRate) || 0) / 100
        return Math.pow(1 + g, year)
      }
      let f = 1
      for (let k = 1; k <= year; k++) f *= (1 + (Number(this.yearlyRates[k - 1]) || 0) / 100)
      return f
    },
    updateChart() {
      const ppy = this.periodsPerYear
      const n = Math.max(1, Math.min(Number(this.nPeriods) || 1, this.maxPeriods))
      const weights = this.seasonalWeights()
      const labels = []
      const data = []
      for (let i = 0; i < n; i++) {
        const year = Math.floor(i / ppy)
        const sub = i % ppy
        const annual = this.marketSize * this.yearFactor(year)
        data.push(Math.round(annual * weights[sub]))
        const yLabel = this.startYear + year
        if (ppy === 1) labels.push(String(yLabel))
        else if (ppy === 2) labels.push(`S${sub + 1} ${yLabel}`)
        else labels.push(`Q${sub + 1} ${yLabel}`)
      }
      this.chartData = { ...this.chartData, labels, datasets: [{ ...this.chartData.datasets[0], data }] }
    },
  },
  watch: {
    filters: { handler() { this.fetchData() }, deep: true },
    marketSize(value) { this.$emit('updateMarketSize', value) },
  },
  created() {
    this.fetchData()
  },
}
</script>

<style scoped>
.empty-projection {
  border: 1px dashed #D6DAE3;
  border-radius: 10px;
  padding: 28px 24px;
  text-align: center;
  background: #FAFAFB;
}
</style>
<style>
canvas {
  aspect-ratio: unset !important;
}
</style>
